<template>
    <div class="social-popup-container">
        <popup-modal @setAction="handleSetAction" @cancelAction="handleCancelAction">
            <div class="popup-wrapper">
                <p class="title">Socials</p>
                <p class="subtitle">Link up your venue's social media handles.</p>
                <div class="facebook-input">
                    <label for="facebook">Facebook</label>
                    <input
                        v-model="formValues.Facebook"
                        :class="formErrors.Facebook.error ? 'invalid-input' : ''"
                        @input="validate('Facebook')"
                        type="text"
                        maxlength="50"
                        name="facebook"
                        placeholder="Enter your handle"
                    />
                    <div v-if="formErrors.Facebook.error" class="input-err-msg">
                        {{ formErrors.Facebook.msg }}
                    </div>
                </div>
                <div class="instagram-input">
                    <label for="instagram">Instagram</label>
                    <input
                        v-model="formValues.Instagram"
                        :class="formErrors.Instagram.error ? 'invalid-input' : ''"
                        @input="validate('Instagram')"
                        type="text"
                        maxlength="50"
                        name="instagram"
                        placeholder="Enter your handle"
                    />
                    <div v-if="formErrors.Instagram.error" class="input-err-msg">
                        {{ formErrors.Instagram.msg }}
                    </div>
                </div>
                <div class="yelp-input">
                    <label for="yelp">Yelp</label>
                    <input
                        v-model="formValues.Yelp"
                        :class="formErrors.Yelp.error ? 'invalid-input' : ''"
                        @input="validate('Yelp')"
                        type="text"
                        maxlength="50"
                        name="yelp"
                        placeholder="Enter your handle"
                    />
                    <div v-if="formErrors.Yelp.error" class="input-err-msg">
                        {{ formErrors.Yelp.msg }}
                    </div>
                </div>
                <div class="tiktok-input">
                    <label for="tiktok">TikTok</label>
                    <input
                        v-model="formValues.Tiktok"
                        :class="formErrors.Tiktok.error ? 'invalid-input' : ''"
                        @input="validate('Tiktok')"
                        type="text"
                        maxlength="50"
                        name="tiktok"
                        placeholder="Enter your handle"
                    />
                    <div v-if="formErrors.Tiktok.error" class="input-err-msg">
                        {{ formErrors.Tiktok.msg }}
                    </div>
                </div>
                <p v-if="serverErrMsg" class="input-err-msg">
                    {{ serverErrMsg }}
                </p>
            </div>
        </popup-modal>
    </div>
</template>
<script>
    import axios from 'axios'
    import PopupModal from '@/components/user/login-popups/PopupModal.vue'

    const URLS = {
        user: '/profile/v1/settings/user/',
        social: ['/profile/social/link/', '/role/venue/']
    }

    const inputValid = {
        Facebook: (val) =>
            !!(val === '' || /^(http:\/\/|https:\/\/)?(www.)?[Ff]acebook.com\/.+$/.test(val)),
        Instagram: (val) =>
            !!(val === '' || /^(http:\/\/|https:\/\/)?(www.)?[Ii]nstagram.com\/.+$/.test(val)),
        Yelp: (val) =>
            !!(val === '' || /^(http:\/\/|https:\/\/)?(www.)?[Yy]elp.com\/.+$/.test(val)),
        Tiktok: (val) =>
            !!(val === '' || /^(http:\/\/|https:\/\/)?(www.)?[Tt]ik[Tt]ok.com\/.+$/.test(val))
    }

    export default {
        name: 'Social',

        components: {
            'popup-modal': PopupModal
        },

        emits: ['closeModalAction'],

        data: () => ({
            formValues: {
                Facebook: '',
                Instagram: '',
                Yelp: '',
                Tiktok: ''
            },

            formErrors: {
                Facebook: {
                    error: false,
                    msg: 'Please enter with format: www.facebook.com/xxx'
                },
                Instagram: {
                    error: false,
                    msg: 'Please enter with format: www.instagram.com/xxx'
                },
                Yelp: {
                    error: false,
                    msg: 'Please enter with format: www.yelp.com/xxx'
                },
                Tiktok: {
                    error: false,
                    msg: 'Please enter with format: www.tiktok.com/xxx'
                }
            },

            serverErrMsg: ''
        }),

        methods: {
            async handleSetAction() {
                if (this.validate()) {
                    let socialData = this.prepSocialData()

                    if (Object.keys(socialData).length) {
                        await this.updateSocialLinks(socialData)
                        this.$emit('closeModalAction')
                    } else {
                        this.serverErrMsg =
                            '[422] : You must enter at least one social link to continue.'
                    }
                }
            },

            handleCancelAction() {
                for (const key of Object.keys(this.formValues)) {
                    this.$set(this.formValues, key, '')
                }
                for (const [key, val] of Object.entries(this.formErrors)) {
                    this.$set(this.formErrors, key, { ...val, error: false })
                }

                this.$emit('closeModalAction')
            },

            validate(name) {
                let keys = Object.keys(this.formErrors)

                if (name) return this.validateInput(name)

                let isInputValid = true

                for (let i = 0; i < keys.length; i++) {
                    if (!inputValid[keys[i]](this.formValues[keys[i]])) {
                        this.formErrors[keys[i]].error = true
                        isInputValid = false
                    } else {
                        this.formErrors[keys[i]].error = false
                    }
                }

                return isInputValid
            },

            validateInput(name) {
                if (!inputValid[name](this.formValues[name])) {
                    this.formErrors[name].error = true
                    return false
                } else {
                    this.formErrors[name].error = false
                    return true
                }
            },

            prepSocialData() {
                let socialData = Object.entries(this.formValues).filter(
                    // eslint-disable-next-line
                    ([k, v]) => v !== ''
                )
                let data = {}

                for (let i = 0; i < socialData.length; i++) {
                    let key = socialData[i][0]
                    let value = socialData[i][1]

                    data[key] = value
                }

                return data
            },

            async updateSocialLinks(data) {
                try {
                    let userResp = await axios.get(this.URLS.user)
                    let userId = userResp.data.data[0].id

                    let socialResp = await axios.put(
                        this.URLS.social[0] + userId + this.URLS.social[1],
                        data
                    )
                    return socialResp
                } catch (err) {
                    this.serverErrMsg = `[${err.response?.status}] : ${err.response?.data?.message}`

                    throw err
                }
            }
        },

        computed: {
            URLS: () => URLS
        }
    }
</script>
<style lang="scss" scoped>
    .popup-wrapper {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        margin-left: 90px;

        .title {
            margin-top: 50px;
            margin-bottom: 0px;
            color: #dedede;
            font-family: Instrument Sans;
            font-size: 44px;
            font-style: normal;
            font-weight: 500;
            line-height: 110%;
            letter-spacing: -1.76px;
        }

        .subtitle {
            margin-top: 22px;
            margin-bottom: 0px;
            color: #acacac;
            font-family: Instrument Sans;
            font-size: 16px;
            font-style: normal;
            font-weight: 550;
            line-height: 125%;
        }

        label {
            color: #8b8b8b;
            overflow: hidden;
            text-overflow: ellipsis;
            font-family: Instrument Sans;
            font-size: 20px;
            font-style: normal;
            font-weight: 550;
            line-height: 120%;
            letter-spacing: -0.2px;
            margin-top: 25px;
            margin-bottom: 10px;
            display: block;
        }

        input {
            min-width: 980px;
            min-height: 24px;
            border-radius: 12px;
            border: 1.5px solid #8b8b8b;
            background: #262626;
            color: #8b8b8b;
            padding: 12px 15px 15px 15px;
            font-size: 20px;
            font-family: Instrument Sans;
            font-style: normal;
            font-weight: 550;
            line-height: 120%;
            letter-spacing: -0.2px;
        }

        input::placeholder {
            color: #8b8b8b;
            font-size: 20px;
            font-family: Instrument Sans;
            font-style: normal;
            font-weight: 550;
            line-height: 120%;
            letter-spacing: -0.2px;
        }
    }
</style>
